// Copy, with a check and "Copied" for a moment after the tap so it is clear
// the link is on the clipboard.

import { useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode } from "react";
import { CheckIcon } from "./icons";

interface CopyButtonProps {
  readonly text: string;
  readonly className?: string;
  readonly label?: string;
  /** Icon-only: the glyph takes the place of the label text, which becomes
   * the accessible name instead (QUEUE row 120: icons, not call/FaceTime-style
   * text, on the controls over a phone call). */
  readonly icon?: ReactNode;
  readonly style?: CSSProperties;
}

export function CopyButton({ text, className = "btn", label = "Copy", icon, style }: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const copy = (): void => {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {
        // Clipboard refused (an old browser or a denied permission): select
        // nothing and leave the button as it was. Share still works.
      });
  };

  return (
    <button
      type="button"
      className={className}
      onClick={copy}
      aria-live="polite"
      aria-label={icon ? label : undefined}
      title={icon ? label : undefined}
      style={style}
    >
      {copied ? <CheckIcon size={16} /> : icon}
      {icon ? null : copied ? "Copied" : label}
    </button>
  );
}

/** The OS share sheet, where there is one. */
export function canShare(): boolean {
  return typeof navigator.share === "function";
}

export function shareLink(url: string, title: string): void {
  void navigator.share({ title, url }).catch(() => {
    // Dismissing the sheet rejects; nothing to do.
  });
}
